import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ListeDepotsReponse, AccesDeposantReponse } from '@artosera/shared';
import { api, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';
import { useMutation } from './mutation.js';
const racine = (g: string) => `/g/${encodeURIComponent(g)}/oeuvres-confiees`;
export interface FiltresDepots {
  deposantId?: string;
  oeuvreId?: string;
  etat?: string;
  enRetard?: boolean;
}
export const deposantApi = {
  liste: (g: string, filtres: FiltresDepots, appel: AppelApi) =>
    api.get<ListeDepotsReponse>(
      `${racine(g)}?${new URLSearchParams(
        Object.entries(filtres)
          .filter(([, v]) => v !== undefined && v !== '')
          .map(([k, v]) => [k, String(v)]),
      )}`,
      { galerie: g, ...appel },
    ),
  creer: (g: string, corps: unknown, appel: AppelApi) =>
    api.post(`${racine(g)}`, corps, { galerie: g, ...appel }),
  geste: (
    g: string,
    id: string,
    geste: 'rendue' | 'reglement' | 'annuler-reglement',
    corps: unknown,
    appel: AppelApi,
  ) => api.post(`${racine(g)}/${encodeURIComponent(id)}/${geste}`, corps, { galerie: g, ...appel }),
  acces: (g: string, id: string, appel: AppelApi) =>
    api.get<AccesDeposantReponse>(`${racine(g)}/contacts/${encodeURIComponent(id)}/acces`, {
      galerie: g,
      ...appel,
    }),
  inviter: (g: string, id: string, appel: AppelApi) =>
    api.post(
      `${racine(g)}/contacts/${encodeURIComponent(id)}/invitation`,
      {},
      { galerie: g, ...appel },
    ),
  accesGeste: (g: string, id: string, geste: string, corps: unknown, appel: AppelApi) =>
    api.post(`${racine(g)}/acces/${encodeURIComponent(id)}/${geste}`, corps, {
      galerie: g,
      ...appel,
    }),
};
export function useDepots(g: string, filtres: FiltresDepots) {
  return useQuery({
    queryKey: cleMetier(g, 'oeuvres-confiees', filtres),
    queryFn: (c) => deposantApi.liste(g, filtres, appelDeRequete(c)),
  });
}
export function useAccesDeposant(g: string, id: string) {
  return useQuery({
    queryKey: cleMetier(g, 'oeuvres-confiees', 'acces', id),
    queryFn: (c) => deposantApi.acces(g, id, appelDeRequete(c)),
  });
}
export function useGesteDeposant<V>(
  g: string,
  action: (v: V, appel: AppelApi) => Promise<unknown>,
) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: action,
      onSuccess: () => client.invalidateQueries({ queryKey: cleMetier(g, 'oeuvres-confiees') }),
    }),
  );
}
